import styled from "@emotion/styled";
import { motion } from "motion/react";
import { FC, useCallback } from "react";
import { useParams } from "react-router-dom";

import { appRoutes } from "@/app/router/appRoutes";
import { FullScreenOverlay } from "@/components/full-screen-overlay";
import { windowModule } from "@/modules/window";
import { FeatureEnrollmentContent } from "@/pages/feature-enrollment/feature-enrollment-content";
import { useFeatureEnrollmentPageState } from "@/pages/feature-enrollment/feature-enrollment-page.hooks";
import { resolveFeatureEnrollmentRedirectPath } from "@/pages/feature-enrollment/feature-enrollment.utils";
import type { FeatureEnrollmentLinkSummary } from "@/pages/feature-enrollment/feature-enrollment.utils";
import { useAppStore } from "@/store";

export const FeatureEnrollmentPage: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { store } = useAppStore();
  const viewerName =
    store.account.myAccount?.profileDisplayName ??
    store.account.myAccount?.profileEmailAddress ??
    null;

  const { kind, link, isAccepting, acceptError, statusTitle, statusBody, handleAccept } =
    useFeatureEnrollmentPageState({ slug });

  const navigateToLinkDestination = useCallback((targetLink: FeatureEnrollmentLinkSummary) => {
    const redirectPath = resolveFeatureEnrollmentRedirectPath(targetLink.redirectPath);
    const redirectUrl = windowModule.buildUrl({ path: redirectPath });

    void windowModule.navigateToAndRefresh({ url: redirectUrl });
  }, []);

  const handleDismiss = useCallback(() => {
    const homeUrl = windowModule.buildUrl({ path: appRoutes.notesList({}).path });

    void windowModule.navigateToAndRefresh({ url: homeUrl });
  }, []);

  const handleContinue = useCallback(() => {
    if (!link) {
      handleDismiss();
      return;
    }

    navigateToLinkDestination(link);
  }, [handleDismiss, link, navigateToLinkDestination]);

  const handleAcceptAndRedirect = useCallback(async () => {
    const acceptedLink = await handleAccept();

    if (!acceptedLink) {
      return;
    }

    navigateToLinkDestination(acceptedLink);
  }, [handleAccept, navigateToLinkDestination]);

  return (
    <FullScreenOverlay>
      <MotionDiv initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
        <FeatureEnrollmentContent
          viewKind={kind}
          link={link}
          isAccepting={isAccepting}
          acceptError={acceptError}
          statusTitle={statusTitle}
          statusBody={statusBody}
          viewerName={viewerName}
          onAccept={handleAcceptAndRedirect}
          onContinue={handleContinue}
          onDismiss={handleDismiss}
        />
      </MotionDiv>
    </FullScreenOverlay>
  );
};

const MotionDiv = styled(motion.div)`
  min-width: 100%;
  min-height: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -100px;
`;
